import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@noor/lib';
import { isDeliveryForbiddenError } from '@/shared/lib/delivery-errors';
import {
  useLegacyOrders,
  legacyOrderApi,
  legacyOrderKeys,
  LegacyImageLightbox,
  type LegacyOrder,
} from '@/entities/legacy-order';
import { LegacyRoot, LegacyPageHeader } from '@/entities/legacy-mode';
import {
  addDays,
  formatWireDate,
  type LegacyDateRange,
} from '@/entities/legacy-date-range';
import { LegacyOrdersToolbar } from '@/widgets/legacy-orders-toolbar';
import { LegacyStatusFilter } from '@/widgets/legacy-status-filter';
import { LegacyOrdersTable, LEGACY_ORDERS_COLUMNS } from '@/widgets/legacy-orders-table';
import { LegacyCancelOrderDialog } from '@/widgets/legacy-cancel-order-dialog';
import { LegacyConfirmDialog } from '@/widgets/legacy-confirm-dialog';
import { LegacyTablePager, LEGACY_PAGE_SIZE_OPTIONS } from '@/widgets/legacy-table-pager';
import { useLegacySocket, useDeferredRefresh } from '@/entities/legacy-socket';
import { LegacyOrderDrawer } from '@/widgets/legacy-order-drawer';
import {
  readStoredColumnVisibility,
  storeColumnVisibility,
  storeLegacyPageSize,
} from '../lib/table-prefs';

/**
 * The legacy Доставка orders screen — a 1-to-1 reproduction of the old
 * merchant panel's `/delivery` page, rendered when legacy mode is on.
 *
 * The page is a thin composition (toolbar · status pills · table ·
 * pagination); the URL owns status/search/page/limit, matching how the
 * original pushed its filters into the query string so a filtered list stayed
 * shareable and survived a reload.
 *
 * DATE RANGE. Defaults to the current week (Monday → today, the original's
 * `startOf("week")..tomorrow` window) and is overridable through the picker.
 * The chosen range lives in the URL so a filtered view stays shareable, like
 * every other filter here.
 */
interface LegacyOrdersPageProps {
  status: string;
  search: string;
  page: number;
  /** Rows per page — URL-driven, seeded from the stored table preference. */
  limit: number;
  /** URL-bound window; falls back to the default week when absent. */
  range: LegacyDateRange;
  onStatusChange: (status: string) => void;
  onSearchChange: (search: string) => void;
  onRangeChange: (range: LegacyDateRange) => void;
  onPageChange: (page: number) => void;
  /** A size change also restarts paging — the route resets `page` with it. */
  onLimitChange: (limit: number) => void;
}

export function LegacyOrdersPage({
  status,
  search,
  page,
  limit,
  range,
  onStatusChange,
  onSearchChange,
  onRangeChange,
  onPageChange,
  onLimitChange,
}: LegacyOrdersPageProps) {
  const { t } = useTranslation('legacy');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const query = {
    status,
    search: search || undefined,
    fromDate: formatWireDate(range.from),
    // The endpoint's end bound is EXCLUSIVE, so the selected last day is only
    // included when we send the day AFTER it.
    tillDate: formatWireDate(addDays(range.to, 1)),
    skip: (page - 1) * limit,
    limit,
  };

  const { data, isLoading, isFetching, isError, refetch } = useLegacyOrders(query);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: legacyOrderKeys.lists() });

  /** Void-returning wrapper — the socket callback is not awaited. */
  const invalidateLists = () => void invalidate();

  /**
   * The order whose detail drawer is open, or null. Also the socket's
   * suppression signal — see below.
   */
  const [openOrderId, setOpenOrderId] = useState<number | null>(null);

  /**
   * The receipt lightbox's image paths (storage-relative), or null while
   * closed — the old page's `uploadImages` state, held HERE because the
   * overlay must layer over the whole screen while the thumbnail that opens
   * it lives deep inside a table cell.
   */
  const [receiptImages, setReceiptImages] = useState<string[] | null>(null);

  /**
   * Column visibility for the grid, TanStack shape (`id → visible`), owned by
   * the page so the toolbar's selector and the table read one source. Seeded
   * from and persisted to localStorage — the old panel kept the same choice in
   * a redux-persist slice.
   */
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() =>
    readStoredColumnVisibility(),
  );

  const toggleColumn = (id: string) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [id]: prev[id] === false };
      storeColumnVisibility(next);
      return next;
    });
  };

  const resetColumns = () => {
    setColumnVisibility({});
    storeColumnVisibility({});
  };

  /**
   * Live updates. The backend pushes `{"type":"refresh"}` whenever an order
   * changes status, which is what keeps the list current without polling.
   *
   * The refetch is HELD while the detail drawer is open, reproducing the
   * original's `refetchOrders && !isDrawerOpen` gate: refetching underneath an
   * open drawer re-sorts and re-pages the list being read. Held pushes are not
   * dropped — they collapse into one refetch when the drawer closes.
   */
  const signalRefresh = useDeferredRefresh(invalidateLists, {
    suppressed: openOrderId !== null,
  });

  useLegacySocket({ onRefresh: signalRefresh, enabled: true });

  /**
   * Re-dispatching the courier search goes through a confirmation first, as
   * the original did (`ActionCellRenderer.tsx:36-46` opened the dialog; the
   * page's confirm ran the mutation). The original ALSO re-checked the
   * profile's `is_business` before confirming and force-logged-out otherwise —
   * that guard belongs to the old auth layer, which this port deliberately
   * does not reproduce, so the confirm goes straight to the mutation.
   */
  const [pendingReorder, setPendingReorder] = useState<LegacyOrder | null>(null);

  const reorder = useMutation({
    mutationFn: (order: LegacyOrder) => legacyOrderApi.reOrderCourier(order.id),
    onSuccess: () => {
      toast.success(
        // Key not in the legacy locale yet — the default carries the old
        // panel's own ru string until it lands.
        t('toast.re_order_success', {
          defaultValue: 'Повторный заказ курьера прошел успешно',
        }),
      );
      setPendingReorder(null);
      void invalidate();
    },
    onError: (error) => {
      if (isDeliveryForbiddenError(error)) {
        setPendingReorder(null);
        return;
      }
      // DELIBERATE FIX: the old page showed this error through a SUCCESS
      // toast (`index.tsx:114-116` called `success(...)` in `onError`). The
      // text is the old error text; the tone is corrected.
      toast.error(
        t('toast.re_order_error', {
          defaultValue: 'Ошибка при перезаказе курьера!',
        }),
      );
      setPendingReorder(null);
    },
  });

  /**
   * Cancellation always goes through the reason dialog — never straight from
   * the row action. Cancelling is irreversible and the reason is recorded on
   * the order, so the row button only OPENS the dialog and this mutation is
   * what the dialog's confirm calls.
   */
  const [pendingCancel, setPendingCancel] = useState<LegacyOrder | null>(null);

  const cancel = useMutation({
    mutationFn: ({ order, reason }: { order: LegacyOrder; reason: string }) =>
      legacyOrderApi.cancelOrder({ id: order.id, reason }),
    onSuccess: () => {
      toast.success(t('toast.cancel_order_success'));
      setPendingCancel(null);
      void invalidate();
    },
    onError: (error) => {
      if (!isDeliveryForbiddenError(error)) toast.error(t('toast.cancel_order_error'));
    },
  });

  /**
   * Settling a courier's product debt.
   *
   * Behind a confirmation because it moves MONEY: it tells the backend the
   * merchant has been paid for goods the courier fronted, and there is no undo.
   * The source gates it the same way (`ActionCellRenderer.tsx:17-35` opens a
   * confirm dialog rather than mutating on click).
   *
   * `7` is the "settled, pending review" state the source PATCHes to — not a
   * terminal paid state, which only the backend sets once the transfer clears.
   */
  const [pendingSettle, setPendingSettle] = useState<LegacyOrder | null>(null);

  const settleDebt = useMutation({
    mutationFn: (order: LegacyOrder) =>
      legacyOrderApi.changePaymentStatus({
        orderId: order.id,
        courierPaymentStatus: 7,
      }),
    onSuccess: () => {
      toast.success(t('toast.settle_debt_success'));
      setPendingSettle(null);
      void invalidate();
    },
    onError: (error) => {
      if (!isDeliveryForbiddenError(error)) toast.error(t('toast.settle_debt_error'));
    },
  });

  const orders = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  /**
   * Page count is DERIVED from the total and the page size we actually asked
   * for — never taken from the response's `last_page`.
   *
   * The old backend computes `last_page` against its OWN default page size,
   * not the `limit` the request carried, so the two disagree the moment the
   * merchant picks a different size: 86 orders at `limit=15` answered
   * `last_page: 29` (86 ÷ 3, its internal default), offering 29 pages for a
   * list that only has 6. Pages past the real end then queried a `skip`
   * beyond the result set and the counter read "Показано 121 - 86 из 86".
   *
   * `Math.ceil(count / limit)` is the same derivation the reports screen
   * already uses, and it stays correct whatever the backend defaults to.
   */
  const lastPage = Math.max(1, Math.ceil(totalCount / limit));

  /**
   * Snap a stranded page back into range.
   *
   * The page number is URL state, so it survives changes that SHRINK the
   * result set — a narrower date window, a status pill with fewer rows, a
   * bookmarked `?page=9`. Left alone the list would query a `skip` past the
   * end and render empty rows under a pager pointing nowhere. An effect rather
   * than a render-time redirect: the count is only known once the response
   * lands.
   */
  useEffect(() => {
    // Gated on `data` — `totalCount` is 0 while the first fetch is in flight,
    // which would bounce every visit back to page 1 before the rows arrive.
    if (data && page > lastPage) onPageChange(lastPage);
  }, [data, page, lastPage, onPageChange]);

  return (
    <LegacyRoot
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      containerVariant="fill"
      // Full-bleed like the old `/delivery` page: its tab bar and table span
      // the viewport width, not the app's 1600px reading column. Both live in
      // the same padded column below, so widening keeps them fitted to each
      // other.
      contained={false}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <LegacyPageHeader title={t('top_bar.delivery')} />
        <LegacyOrdersToolbar
          search={search}
          range={range}
          onRangeChange={(next) => {
            // A new window restarts paging, like a status or search change.
            onRangeChange(next);
            onPageChange(1);
          }}
          onSearchChange={(next) => {
            // A new search restarts paging — page 3 of the old result set is
            // meaningless against the new one.
            onSearchChange(next);
            onPageChange(1);
          }}
          onNewOrder={() => void navigate({ to: '/delivery/orders/create' })}
          columns={LEGACY_ORDERS_COLUMNS}
          columnVisibility={columnVisibility}
          onToggleColumn={toggleColumn}
          onResetColumns={resetColumns}
        />

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-4 py-3 sm:px-6">
          <div className="shrink-0">
            <LegacyStatusFilter
              selected={status}
              counts={data?.count_info}
              onSelect={(next) => {
                onStatusChange(next);
                onPageChange(1);
              }}
            />
          </div>

          {isError && !data ? (
            <div className="flex flex-col items-center gap-3 py-24 text-center">
              <p className="text-[16px] font-medium text-[var(--lg-grey-700)]">
                {t('error.title')}
              </p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="cursor-pointer rounded-[96px] bg-[var(--lg-orange-500)] px-4 py-2 text-[16px] font-semibold text-[var(--lg-surface)]"
              >
                {t('error.retry')}
              </button>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <LegacyOrdersTable
                orders={orders}
                isLoading={isLoading}
                isFetching={isFetching}
                columnVisibility={columnVisibility}
                onOpenOrder={setOpenOrderId}
                onReorder={setPendingReorder}
                onCancel={setPendingCancel}
                onSettleDebt={setPendingSettle}
                onShowReceipt={setReceiptImages}
                // Duplicating opens the SAME screen as editing, at `?copy=true`
                // — the old panel's own route shape.
                onDuplicate={(o) =>
                  void navigate({
                    to: '/delivery/orders/$orderId/edit',
                    params: { orderId: String(o.id) },
                    search: { copy: true },
                  })
                }
                className="min-h-0 flex-1"
              />

              {/* The old bottom bar rendered whenever the table had ROWS —
                  even on a single page — so the gate is on rows, not on
                  pageCount > 1. */}
              {orders.length > 0 && (
                <div className="shrink-0">
                  <LegacyTablePager
                    page={page}
                    pageCount={lastPage}
                    totalCount={totalCount}
                    pageSize={limit}
                    onPageChange={onPageChange}
                    onPageSizeChange={(size) => {
                      // Mirror the choice into the old panel's own storage key
                      // so it survives a reload (and round-trips with the old
                      // app on the same origin); the URL drives the query.
                      storeLegacyPageSize(size);
                      onLimitChange(size);
                    }}
                    pageSizeOptions={LEGACY_PAGE_SIZE_OPTIONS}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <LegacyOrderDrawer
        orderId={openOrderId}
        onClose={() => {
          setOpenOrderId(null);
          // The old page refetched the list on every drawer close
          // (`index.tsx:373-376`) — the drawer's actions (cancel, evaluate,
          // pay) change the very row the list shows. The deferred socket
          // refresh also flushes here, but an explicit invalidate covers
          // closes with no socket push.
          void invalidate();
        }}
      />

      <LegacyConfirmDialog
        open={pendingReorder !== null}
        title={t('confirm_dialog.re_order_title')}
        description={t('confirm_dialog.re_order_desc')}
        confirmLabel={t('confirm_dialog.confirm')}
        cancelLabel={t('confirm_dialog.cancel')}
        confirmVariant="brand"
        isPending={reorder.isPending}
        onConfirm={() => {
          if (pendingReorder) reorder.mutate(pendingReorder);
        }}
        onCancel={() => setPendingReorder(null)}
      />

      <LegacyConfirmDialog
        open={pendingSettle !== null}
        // The ru value of `edit_payment_status` already ends with `?`, so
        // nothing is appended after the number (the old page's trailing `?`
        // came from a locale whose value didn't carry one).
        title={`${t('confirm_dialog.edit_payment_status')}${pendingSettle ? ` #${pendingSettle.display_id}` : ''}`}
        confirmLabel={t('confirm_dialog.confirm')}
        cancelLabel={t('confirm_dialog.cancel')}
        confirmVariant="brand"
        isPending={settleDebt.isPending}
        onConfirm={() => {
          if (pendingSettle) settleDebt.mutate(pendingSettle);
        }}
        onCancel={() => setPendingSettle(null)}
      />

      <LegacyCancelOrderDialog
        open={pendingCancel !== null}
        {...(pendingCancel ? { orderNumber: String(pendingCancel.display_id) } : {})}
        isPending={cancel.isPending}
        onConfirm={(reason) => {
          if (pendingCancel) cancel.mutate({ order: pendingCancel, reason });
        }}
        onCancel={() => setPendingCancel(null)}
      />

      <LegacyImageLightbox
        images={receiptImages}
        onClose={() => setReceiptImages(null)}
      />
    </LegacyRoot>
  );
}
